import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import ReportLayout from '../../components/Layout/ReportLayout';
import styles from '../../assets/styles/report/CreativeAnalysis.module.scss';
import { motion } from 'framer-motion';
import { useReportFilter } from '../../contexts/ReportContext';
import { useProjectContext } from "../../contexts/ProjectContext";

interface CreativeBannerData {
  id: string;
  assetId: number | string;
  campaignId: number | string;
  campaignName: string;
  name: string;
  type: 'Image' | 'Video';
  format: string;
  platform: string;
  thumbnailUrl: string | null;
  approvalStatus: string;
  ad_status: string;
  performanceLabel: string;
  impressions: number;
  clicks: number;
  spend: number;
  ctr: string;
  cvr: string;
  cpc: number;
}

// 審査ステータスカラーヘルパー関数
const getApprovalStatusColor = (status: string): string => {
  switch ((status || '').toUpperCase()) {
    case 'APPROVED':
      return '#2AB6BF';
    case 'UNDER_REVIEW':
    case 'APPROVED_LIMITED':
      return '#EFBB32';
    case 'DISAPPROVED':
      return '#F2065D';
    case 'UNKNOWN':
    case 'UNSPECIFIED':
    default:
      return '#969696';
  }
};

// 配信ステータスカラーヘルパー関数
const getAdStatusColor = (status: string): string => {
  switch ((status || '').toUpperCase()) {
    case 'ENABLED':
      return '#2AB6BF';
    case 'ACTIVE':
      return '#2AB6BF';
    case 'PAUSED':
    case 'REMOVED':
    default:
      return '#969696';
  }
};

// 評価（パフォーマンスラベル）カラーヘルパー関数
const getPerformanceLabelColor = (label: string): string => {
  switch ((label || '').toUpperCase()) {
    case 'BEST':
    case 'GOOD':
      return '#2AB6BF';
    case 'LOW':
    case 'LEARNING':
      return '#EFBB32';
    case 'PENDING':
    case 'UNKNOWN':
    case 'UNSPECIFIED':
    default:
      return '#969696';
  }
};

const CreativeAnalysis: React.FC = () => {
  const { selectedProjectId } = useProjectContext();
  const { selectedPeriod, customStart, customEnd } = useReportFilter();

  const [selectedMedia, setSelectedMedia] = useState('All');
  const [selectedSize, setSelectedSize] = useState('All');
  const [selectedPlatform, setSelectedPlatform] = useState('All');

  const [bannerData, setBannerData] = useState<CreativeBannerData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState<CreativeBannerData | null>(null);


  useEffect(() => {
    const fetchCreativeData = async () => {
      setIsLoading(true);
      if (!selectedProjectId) return;
      try {
        const params: Record<string, string> = {
          project_id: String(selectedProjectId),
          period: selectedPeriod,
          media: selectedMedia,
          size: selectedSize,
          platform: selectedPlatform,
        };
        if (selectedPeriod === 'custom' && customStart && customEnd) {
          params.start = customStart;
          params.end = customEnd;
        }
        const queryParams = new URLSearchParams(params);
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/get-report-creative?${queryParams}`,
          { credentials: 'include' }
        );
        const data = await response.json();
        setBannerData(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Creative data fetch error:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCreativeData();
  }, [selectedProjectId, selectedPeriod, customStart, customEnd, selectedMedia, selectedSize, selectedPlatform]);

  return (
    <ReportLayout>
      <div className={styles.reportContent}>

        {/* Header Area */}
        <div className={styles.headerArea}>
          <div>
            <h2 className={styles.pageTitle}>クリエイティブ分析</h2>
            <p className={styles.pageDescription}>各クリエイティブ（バナー・動画）ごとの配信実績と効果指標の分析です。</p>
          </div>

          {/* Filters */}
          <div className={styles.filterContainer}>
            <div className={styles.filterGroup}>
              <span className={styles.filterLabel}>Platform:</span>
              {['All', 'Google', 'Meta', 'Yahoo'].map(p => (
                <button
                  key={p}
                  onClick={() => setSelectedPlatform(p)}
                  className={`${styles.filterBtn} ${selectedPlatform === p ? styles.active : ''}`}
                >
                  {p}
                </button>
              ))}
            </div>

            <div className={styles.filterGroup}>
              <span className={styles.filterLabel}>Media:</span>
              {['All', 'Image', 'Video'].map(m => (
                <button
                  key={m}
                  onClick={() => setSelectedMedia(m)}
                  className={`${styles.filterBtn} ${selectedMedia === m ? styles.active : ''}`}
                >
                  {m}
                </button>
              ))}
            </div>

            <div className={styles.filterGroup}>
              <span className={styles.filterLabel}>Size:</span>
              {['All', 'Square', 'Vertical', 'Horizontal'].map(s => (
                <button
                  key={s}
                  onClick={() => setSelectedSize(s)}
                  className={`${styles.filterBtn} ${selectedSize === s ? styles.active : ''}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Header Row */}
        <div className={styles.listHeader}>
          <div className={styles.colThumb}>Thumb</div>
          <div className={styles.colInfo}>クリエイティブ名 / 情報</div>
          <div className={styles.colMetric}>審査</div>
          <div className={styles.colMetric}>配信</div>
          <div className={styles.colMetric}>評価</div>
          <div className={styles.colMetric}>Imp</div>
          <div className={styles.colMetric}>Clicks</div>
          <div className={styles.colMetric}>CTR</div>
          <div className={styles.colMetric}>CVR</div>
          <div className={styles.colMetric}>CPC</div>
        </div>

        {/* Banner List */}
        <div className={styles.listBody}>
          {isLoading ? (
            <div className={styles.noContent}>読み込み中...</div>
          ) : bannerData.length === 0 ? (
            <div className={styles.noContent}>データがありません</div>
          ) : (
            bannerData.map((banner, index) => (
              <motion.div
                key={banner.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={styles.bannerRow}
              >
                {/* 1. Thumbnail (Left) */}
                <div className={styles.thumbnail}>
                  {banner.thumbnailUrl ? (
                    <img
                      src={banner.thumbnailUrl}
                      alt={banner.name || "thumbnail"}
                      className={styles.thumbnailImg}
                      onClick={() => setSelectedBanner(banner)}
                    />
                  ) : (
                    <>
                      <div className={styles.thumbPlaceholder}></div>
                      <span className={styles.thumbType}>No Image</span>
                    </>
                  )}
                </div>

                {/* 2. Info (Middle) */}
                <div className={styles.bannerInfo}>
                  <div className={styles.bannerName}>{`${banner.name}【${banner.type}】`}</div>
                  <div className={styles.campaignName}>
                    {banner.campaignName}
                  </div>
                  <div className={styles.bannerMeta}>
                    <span>{banner.platform}</span>
                    <span>{banner.format}</span>
                    <span>{banner.ad_status}</span>
                    <span>¥{banner.spend.toLocaleString()}</span>
                  </div>
                </div>

                {/* 3. Metrics (Columns) */}
                <div className={styles.metricCell}>
                  <span
                    className={styles.statusIcon}
                    style={{
                      backgroundColor: getApprovalStatusColor(banner.approvalStatus),
                    }}
                    title={`審査: ${banner.approvalStatus}`}
                  />
                </div>

                <div className={styles.metricCell}>
                  <div className={styles.statusWithLabel}>
                    <span
                      className={styles.statusIcon}
                      style={{
                        backgroundColor: getAdStatusColor(banner.ad_status),
                      }}
                      title={`配信: ${banner.ad_status}`}
                    />
                  </div>
                </div>

                <div className={styles.metricCell}>
                  <span
                    className={styles.statusIcon}
                    style={{
                      backgroundColor: getPerformanceLabelColor(banner.performanceLabel),
                    }}
                    title={`評価: ${banner.performanceLabel}`}
                  />
                </div>

                <div className={styles.metricCell}>{banner.impressions.toLocaleString()}</div>
                <div className={styles.metricCell}>{banner.clicks.toLocaleString()}</div>
                <div className={`${styles.metricCell} ${styles.blue}`}>{banner.ctr}</div>
                <div className={`${styles.metricCell} ${styles.pink}`}>{banner.cvr}</div>
                <div className={styles.metricCell}>¥{banner.cpc}</div>
              </motion.div>
            ))
          )}
        </div>
      </div>

      {/* 拡大モーダル */}
      {selectedBanner && createPortal(
        <div
          className={styles.creativeModalWrap}
          onClick={() => setSelectedBanner(null)}
        >
          <div
            className={styles.creativeModal}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className={styles.closeBtn}
              onClick={() => setSelectedBanner(null)}
            >
              ×
            </button>
            <div className={styles.bannerName}>
              {`${selectedBanner.name}【${selectedBanner.type}】`}
            </div>
            <div className={styles.campaignName}>
              {selectedBanner.campaignName}
            </div>
            <div className={styles.bannerMeta}>
              <span>{selectedBanner.platform}</span>
              <span>{selectedBanner.format}</span>
              <span>{selectedBanner.ad_status}</span>
              <span>¥{selectedBanner.spend.toLocaleString()}</span>
            </div>
            <div className={styles.bannerDetail}>
              {selectedBanner.thumbnailUrl && (
                <img
                  src={selectedBanner.thumbnailUrl}
                  alt={selectedBanner.name}
                  className={styles.bannerDetailImg}
                />
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

    </ReportLayout>
  );
};

export default CreativeAnalysis;
